/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import PropTypes from 'prop-types'
import colors from '@sportninja/common/constants/appColors'
import dayjs from 'dayjs'
import { ReactComponent as SportNinjaLogoSvg } from 'src/components/MainNav/logo.svg'
import { countTeamShots } from '@sportninja/common/utils/games/shotsCalculator'

const DEFAULT_HOME_COLOR = colors.PRIMARY
const DEFAULT_VISITOR_COLOR = colors.SECONDARY_300

const widgetContainer = css`
  position: absolute;
  top: 16px;
  left: 16px;
  display: flex;
  align-items: stretch;
  background: ${colors.WHITE};
  border-radius: 8px;
  overflow: hidden;
  z-index: 10;
  width: fit-content;
`

const logoCell = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 10px;
  flex-shrink: 0;
`

const middleColumn = css`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`

const teamsRow = css`
  display: flex;
  align-items: stretch;
  flex: 1;
  justify-content: center;
`

/* Team block: full team-color background containing abbr + score */
const teamBlock = css`
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
`

const teamAbbr = css`
  font-size: 12px;
  font-weight: 700;
  color: ${colors.WHITE};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0 8px;
  display: flex;
  align-items: center;
`

/* Score gets a semi-dark overlay on top of the team color */
const scoreInner = css`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  color: ${colors.WHITE};
  min-width: 36px;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.25);
`

const divider = css`
  width: 1px;
  background: rgba(255, 255, 255, 0.2);
  margin: 6px 0;
  flex-shrink: 0;
`

const timeCell = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  justify-content: center;
  padding: 4px 12px;
  flex-shrink: 0;
`

const timeText = css`
  font-size: 13px;
  font-weight: 700;
  color: ${colors.HEADER};
  white-space: nowrap;
`

const periodText = css`
  font-size: 10px;
  font-weight: 500;
  color: ${colors.NEUTRAL_50};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const shotsRow = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 2px 12px 3px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`

const shotsNumber = css`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.HEADER};
`

const shotsLabel = css`
  font-size: 10px;
  font-weight: 800;
  color: ${colors.HEADER};
  text-transform: uppercase;
  letter-spacing: 0.8px;
`

const badgeStyle = css`
  display: inline-block;
  background: ${colors.NEUTRAL_600};
  color: ${colors.WHITE};
  font-size: 9px;
  font-weight: 600;
  padding: 2px 4px;
  border-radius: 2px;
  margin-left: 4px;
  text-transform: uppercase;
`

const LiveScoreWidget = ({
  game,
  homeTeamName,
  visitorTeamName,
  homeTeamColor,
  visitorTeamColor,
}) => {
  if (!game) {
    return null
  }

  const [homeShots, visitorShots] = countTeamShots(
    game.homeTeamId,
    game.visitingTeamId,
    game.shots
  )

  const { completed, started } = game
  const homeColor = homeTeamColor || DEFAULT_HOME_COLOR
  const visitorColor = visitorTeamColor || DEFAULT_VISITOR_COLOR
  const homeAbbr = homeTeamName?.slice(0, 3).toUpperCase() || 'HOM'
  const visitorAbbr = visitorTeamName?.slice(0, 3).toUpperCase() || 'VIS'

  const renderTimeContent = () => {
    if (completed) {
      const isOT = game.isOT || game.overtime
      const isSO = game.isSO || game.shootout
      return (
        <>
          <span css={timeText}>
            Final
            {isOT && <span css={badgeStyle}>OT</span>}
            {isSO && <span css={badgeStyle}>SO</span>}
          </span>
        </>
      )
    }
    if (started) {
      return (
        <>
          <span css={timeText}>{game.currentPeriodTime || '--:--'}</span>
          <span css={periodText}>{game.currentPeriodName || ''}</span>
        </>
      )
    }
    return (
      <>
        <span css={timeText}>{dayjs(game.startsAt).format('h:mm A')}</span>
        <span css={periodText}>{dayjs(game.startsAt).format('MMM DD')}</span>
      </>
    )
  }

  return (
    <div css={widgetContainer}>
      <div css={logoCell}>
        <SportNinjaLogoSvg width={36} height={36} />
      </div>
      <div css={middleColumn}>
        <div css={teamsRow}>
          <div
            css={css`
              ${teamBlock};
              background: ${homeColor};
            `}
          >
            <span css={teamAbbr}>{homeAbbr}</span>
            <div css={scoreInner}>{game.homeTeamScore ?? 0}</div>
          </div>
          <div
            css={css`
              ${teamBlock};
              background: ${visitorColor};
            `}
          >
            <div css={scoreInner}>{game.visitingTeamScore ?? 0}</div>
            <span css={teamAbbr}>{visitorAbbr}</span>
          </div>
        </div>
        <div css={shotsRow}>
          <span css={shotsNumber}>{homeShots}</span>
          <span css={shotsLabel}>Shots</span>
          <span css={shotsNumber}>{visitorShots}</span>
        </div>
      </div>
      <div css={divider} />
      <div css={timeCell}>{renderTimeContent()}</div>
    </div>
  )
}

LiveScoreWidget.propTypes = {
  game: PropTypes.object,
  homeTeamName: PropTypes.string,
  visitorTeamName: PropTypes.string,
  homeTeamColor: PropTypes.string,
  visitorTeamColor: PropTypes.string,
  homeShots: PropTypes.number,
  visitorShots: PropTypes.number,
}

export default LiveScoreWidget
